import { AfterViewInit, Component, ElementRef, Input, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

/**
 * Generic wrapper component for legacy AJS-style modal templates.
 *
 * Renders the imported HTML template string with scope values substituted
 * for {{ expression }} interpolations.  AJS directives (ng-model, ng-if,
 * ng-click) are stripped — the rendered content is read-only display plus
 * close/save buttons wired via DOM event listeners after render.
 */
@Component({
  selector   : 'wor-legacy-modal',
  template: require('./legacy-modal.component.html')
})
export class LegacyModalComponent implements OnInit, AfterViewInit {
  @Input() resolve: any = {};
  @Input() legacyController: (scope: any) => void;
  @Input() title = '';
  @Input() templateHtml = '';

  scope: any = {};
  processedHtml: SafeHtml = null;

  constructor (
    public activeModal: NgbActiveModal,
    private sanitizer: DomSanitizer,
    private elRef: ElementRef,
    private translate: TranslateService,
  ) {}

  ngOnInit (): void {
    Object.assign(this.scope, this.resolve);
    this.scope.$close   = (result?: any) => this.activeModal.close(result);
    this.scope.$dismiss = (reason?: any) => this.activeModal.dismiss(reason);
    this.scope.save     = this.scope.save || (() => this.activeModal.close(this.scope));
    this.scope.close    = this.scope.close || (() => this.activeModal.dismiss());

    if (this.legacyController) {


      this.legacyController(this.scope);


    }

    if (this.templateHtml) {


      this.processedHtml = this.sanitizer.bypassSecurityTrustHtml(
        this.processTemplate(this.templateHtml)
      );


    }
  }

  ngAfterViewInit (): void {
    if (!this.processedHtml) return;

    const el = this.elRef.nativeElement as HTMLElement;

    // Wire up close/dismiss buttons (ng-click="$dismiss()" or ng-click="close()")
    el.querySelectorAll('[data-action]').forEach((btn: HTMLElement) => {
      const action = btn.getAttribute('data-action');
      btn.addEventListener('click', () => {
        if (action === 'close' || action === 'save') {
          this.activeModal.close(this.scope);
        } else {
          this.activeModal.dismiss();
        }
      });
    });
  }

  close(result?: any): void {


    this.activeModal.close(result);


  }
  dismiss(reason?: any): void {

    this.activeModal.dismiss(reason);

  }

  private processTemplate(html: string): string {
    let processed = html
      // Replace <translate translate="KEY"></translate> with translated text
      .replace(/<translate\s+translate="([^"]+)"><\/translate>/g, (_, key) => {
        return this.translate.instant(key) || key;
      })
      // Replace translate="KEY" on buttons/labels with the translated text as content
      .replace(/(<(?:button|span|label|a)[^>]*)\stranslate="([^"]+)"([^>]*>)\s*<\/(?:button|span|label|a)>/g,
        (_, open, key, close) => {
          return `${open}${close}${this.translate.instant(key) || key}</button>`;
        })
      // Replace {{ expr }} interpolations with scope values
      .replace(/\{\{\s*([^}]+)\s*\}\}/g, (_, expr) => {
        return this.evaluateExpr(expr.trim());
      })
      // Convert ng-click="$dismiss()" / ng-click="close()" to data-action
      .replace(/(?:data-)?ng-click="\$dismiss\(\)"/g, 'data-action="dismiss"')
      .replace(/(?:data-)?ng-click="close\(\)"/g, 'data-action="dismiss"')
      .replace(/(?:data-)?ng-click="save\(\)"/g, 'data-action="save"')
      .replace(/(?:data-)?ng-click="\$close\([^)]*\)"/g, 'data-action="close"')
      // Convert ng-model textareas/inputs to show current value
      .replace(/<textarea([^>]*)ng-model="([^"]+)"([^>]*)>(\s*)<\/textarea>/g,
        (_, pre, model, post) => {
          const val = this.evaluateExpr(model) || '';
          return `<textarea${pre}${post}>${val}</textarea>`;
        })
      .replace(/<input([^>]*)ng-model="([^"]+)"([^>]*)\/?>/g,
        (_, pre, model, post) => {
          const val = this.evaluateExpr(model) || '';
          return `<input${pre} value="${val}"${post}>`;
        })
      // Strip remaining AJS directives
      .replace(/\s*(?:data-)?ng-[a-z-]+="[^"]*"/g, '')
      .replace(/\s*(?:data-)?ui-sref="[^"]*"/g, '');

    return processed;
  }

  private evaluateExpr(expr: string): string {
    try {
      // Handle simple property access: item.name, editable, item.note
      const parts = expr.split('.');
      let val: any = this.scope;
      for (const part of parts) {

        if (val == null) return '';

        val = val[part];

      }
      return val != null ? String(val) : '';
    } catch {
      return '';
    }
  }
}
